/* eslint-disable no-control-regex */

import z from "zod";

import {
  ARABIC_TEXT_WITH_NUMBERS_AND_SIGNS_REGEX,
  ARABIC_TEXT_WITH_NUMBERS_REGEX,
  ENGLISH_ADDRESS_EXTENDED_REGEX,
} from "@/utils/constants/arabicRegex";
import { validatePhoneNumber } from "@/utils/validatePhoneNumber";

const phoneRegex = /^[0-9\s\-()+]+$/;

const httpUrl = z
  .string()
  .trim()
  .refine(val => /^https?:\/\/.+/i.test(val), {
    message: "URL must start with http:// or https://",
  })
  .refine(val => z.string().url().safeParse(val).success, {
    message: "Invalid URL format",
  })
  .refine(
    val => {
      try {
        const url = new URL(val);
        const host = url.hostname;
        return host.includes(".") && !host.endsWith(".");
      } catch {
        return false;
      }
    },
    {
      message: "Invalid URL format",
    }
  );

const optionalHttpUrl = z
  .string()
  .trim()
  .nullable()
  .optional()
  .refine(
    val => {
      if (val === null || val === undefined || val === "") return true;
      return httpUrl.safeParse(val).success;
    },
    { message: "URL must be valid and start with http:// or https://" }
  );

export const BranchDetailsSchema = z
  .object({
    id: z.string().optional(),
    referenceNumber: z.string().optional(),
    nameEn: z
      .string()
      .min(1, { message: "Branch name is required." })
      .max(100, { message: "Branch name must be under 100 characters." })
      .refine(value => /^[\u0000-\u007F]+$/.test(value), {
        message: "Only English letters, numbers and symbols are allowed.",
      })
      .trim(),
    nameAr: z
      .string()
      .min(1, { message: "Branch name is required." })
      .max(100, { message: "Branch name must be under 100 characters." })
      .regex(ARABIC_TEXT_WITH_NUMBERS_AND_SIGNS_REGEX, {
        message: "Only Arabic characters and numbers are allowed.",
      })
      .trim(),
    descriptionEn: z
      .string()
      .min(1, { message: "Description (English) is required." })
      .max(500, { message: "Max length is 500 characters." })
      .trim(),

    descriptionAr: z
      .string()
      .min(1, { message: "Description (Arabic) is required." })
      .max(500, { message: "Max length is 500 characters." })
      .regex(ARABIC_TEXT_WITH_NUMBERS_REGEX, {
        message: "Description must contain only Arabic characters and numbers.",
      })
      .trim(),

    phoneCode: z.string().min(1, { message: "Select a country code" }),
    phoneNumber: z
      .string()
      .min(1, { message: "Phone number is required." })
      .refine(val => phoneRegex.test(val), {
        message: "Only digits and common separators allowed",
      }),

    phone: z.string().optional(),
    smsPhoneNumbers: z
      .array(
        z.string().refine(val => validatePhoneNumber(val), {
          message: "Invalid phone number format",
        })
      )
      .optional()
      .nullable()
      .superRefine((val, ctx) => {
        if (!val) return;

        const duplicates = val.filter((v, i) => val.indexOf(v) !== i);

        if (duplicates.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Phone already added",
          });
        }
      }),
    addressEn: z
      .string()
      .min(1, { message: "Address is required." })
      .max(255, { message: "Address must be under 255 characters." })
      .regex(ENGLISH_ADDRESS_EXTENDED_REGEX, {
        message: "Invalid characters in address.",
      })
      .trim(),
    addressAr: z
      .string()
      .min(1, { message: "Address is required." })
      .max(255, { message: "Address must be under 255 characters." })
      .regex(ARABIC_TEXT_WITH_NUMBERS_REGEX, {
        message: "Invalid characters in address.",
      })
      .trim(),
    brandId: z.string({ message: "Brand is required." }),
    legalEntityId: z
      .string()
      .transform(val => (val.trim() === "" ? undefined : val))
      .nullable()
      .optional(),

    googlePlaceId: z.string().optional().nullable(),

    longitude: z.number().optional().nullable(),
    latitude: z.number().optional().nullable(),
    menuUrl: optionalHttpUrl,
    bookingUrl: optionalHttpUrl,
    googlePlaceAverageRating: z.number().optional().nullable(),
    googleTotalReviews: z.number().optional().nullable(),

    reviewSummaryEn: z
      .string()
      .min(1, { message: "Google Reviews Summary (English) is required." })
      .max(500, { message: "Max length is 500 characters." })
      .trim(),

    reviewSummaryAr: z
      .string()
      .min(1, { message: "Google Reviews Summary (Arabic) is required." })
      .max(500, { message: "Max length is 500 characters." })
      .trim(),

    streetId: z
      .string()
      .transform(val => (val.trim() === "" ? undefined : val))
      .nullable()
      .optional(),
    areaId: z
      .string()
      .transform(val => (val.trim() === "" ? undefined : val))
      .nullable()
      .optional(),
    landmarkId: z
      .string()
      .transform(val => (val.trim() === "" ? undefined : val))
      .nullable()
      .optional(),

    cityId: z
      .string({ message: "City is required." })
      .min(1, { message: "City is required." }),
    isTestBranch: z.boolean().optional(),
    brandCountry: z.string().optional(),
    brandCurrency: z.string().optional(),
  })

  .superRefine((data, ctx) => {
    if (!data.brandId) {
      ctx.addIssue({
        path: ["brandId"],
        code: z.ZodIssueCode.custom,
        message: "Brand is required.",
      });
    }

    if (data.phoneCode || data.phoneNumber) {
      const digitsOnly =
        (data.phoneCode?.replace(/\D/g, "") ?? "") +
        (data.phoneNumber?.replace(/\D/g, "") ?? "");
      if (digitsOnly.length > 15) {
        ctx.addIssue({
          path: ["phoneNumber"],
          code: z.ZodIssueCode.custom,
          message: "Phone number must be under 15 characters including code",
        });
      }
    }
  });

export type BranchDetailsType = z.infer<typeof BranchDetailsSchema>;
